import React, { useState, useEffect } from "react"; // Deploy Trigger: Updated Header Logic
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, Menu, X, MessageCircle, Sparkles, ChevronRight, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import { ThemeToggle } from "./ui/theme-toggle";
import api, { waLink } from "../lib/api";
import { cn } from "../lib/utils";
import logo from "../assets/logo.png";

import Fuse from "fuse.js";

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [allPackages, setAllPackages] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [activeMega, setActiveMega] = useState(null);
  const [q, setQ] = useState("");
  const [mouse, setMouse] = useState({ x: -200, y: -200, active: false });
  const headerRef = React.useRef(null);
  const nav = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    api.get("/categories?include_sub=true").then(r => setCategories(r.data));
    api.get("/packages").then(r => setAllPackages(r.data));
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Cherisx-style cursor spotlight on header
  const handleHeaderMove = React.useCallback((e) => {
    const el = headerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setMouse({ x: e.clientX - rect.left, y: e.clientY - rect.top, active: true });
  }, []);
  const handleHeaderLeave = React.useCallback(() => {
    setMouse((m) => ({ ...m, active: false }));
  }, []);

  // Initialize Fuse
  const fuse = React.useMemo(() => new Fuse(allPackages, {
    keys: ["title", "category_name", "category_slug"],
    threshold: 0.4,
  }), [allPackages]);

  // Rotating package per category for mega-menu preview
  const [rotateIdx, setRotateIdx] = useState(0);
  useEffect(() => {
    if (!activeMega) return;
    const t = setInterval(() => setRotateIdx((i) => i + 1), 2500);
    return () => clearInterval(t);
  }, [activeMega]);

  const packagesByCategory = React.useMemo(() => {
    const map = {};
    allPackages.forEach((p) => {
      const k = p.category_slug;
      if (!k) return;
      (map[k] = map[k] || []).push(p);
    });
    return map;
  }, [allPackages]);

  useEffect(() => {
    if (q.trim().length > 1) {
      const results = fuse.search(q).map(r => r.item);
      setSearchResults(results.slice(0, 6));
    } else {
      setSearchResults([]);
    }
  }, [q, allPackages, fuse]);

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) {
      nav(`/search?q=${encodeURIComponent(q.trim())}`);
      setSearchOpen(false);
      setQ("");
    }
  };

  const decorationCategories = categories.filter(c => !c.is_addon && !['activity', 'photograph', 'video', 'cater', 'magician', 'mascot', 'dj-service'].some(s => c.slug.toLowerCase().includes(s)));
  const activityCategories = categories.filter(c => c.is_addon || ['activity', 'photograph', 'video', 'cater', 'magician', 'mascot', 'dj-service'].some(s => c.slug.toLowerCase().includes(s)));

  // Top-level decoration categories shown as their own header items.
  // Drop near-duplicate slugs (couples-special vs couple-s-special) by preferring the one with more subcategories/packages.
  const dedupedDeco = (() => {
    const seen = new Map();
    decorationCategories.forEach((c) => {
      const norm = c.slug.replace(/-/g, "").replace(/s$/, "");
      const score = (c.subcategories?.length || 0) * 10 + (packagesByCategory[c.slug]?.length || 0);
      const prev = seen.get(norm);
      if (!prev || score > prev.score) seen.set(norm, { cat: c, score });
    });
    return Array.from(seen.values()).map((v) => v.cat);
  })();
  const topDecoCats = dedupedDeco.slice(0, 4);
  const moreDecoCats = dedupedDeco.slice(4);

  return (
    <>
      <header
        ref={headerRef}
        onMouseMove={handleHeaderMove}
        onMouseLeave={handleHeaderLeave}
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-in-out px-4 py-3 sm:px-6 lg:px-8",
          isScrolled ? "pt-2" : "pt-6"
        )}
        data-testid="site-header"
      >
        <div 
          className={cn(
            "max-w-7xl mx-auto rounded-3xl transition-all duration-700 ease-in-out border flex items-center justify-between gap-4 px-4 sm:px-8 h-16 sm:h-22 relative cursor-glow",
            isScrolled 
              ? "bg-white/20 backdrop-blur-3xl border-white/30 shadow-[0_20px_50px_rgba(0,0,0,0.1)]" 
              : "bg-black/20 backdrop-blur-3xl backdrop-saturate-150 border-white/20 shadow-2xl shadow-black/40"
          )}
          style={{
            "--mx": `${mouse.x}px`,
            "--my": `${mouse.y}px`,
          }}
        >
          {/* Cursor spotlight wrapper – clipped to header pill so it never overflows but doesn't clip dropdowns */}
          <div className="absolute inset-0 rounded-3xl overflow-hidden pointer-events-none z-0">
            <div
              aria-hidden="true"
              className={cn(
                "absolute inset-0 transition-opacity duration-500",
                mouse.active ? "opacity-100" : "opacity-0"
              )}
              style={{
                background: `radial-gradient(380px circle at ${mouse.x}px ${mouse.y}px, ${
                  isScrolled
                    ? "hsla(308, 80%, 60%, 0.18)"
                    : "hsla(308, 90%, 70%, 0.35)"
                }, transparent 60%)`,
              }}
            />
            <div
              aria-hidden="true"
              className={cn(
                "absolute inset-0 transition-opacity duration-500",
                mouse.active ? "opacity-100" : "opacity-0"
              )}
              style={{
                background: `radial-gradient(180px circle at ${mouse.x}px ${mouse.y}px, hsla(330, 100%, 80%, 0.45), transparent 70%)`,
                mixBlendMode: "overlay",
              }}
            />
          </div>
          {/* Foreground wrapper to keep contents above effects */}
          <div className="relative z-10 flex items-center justify-between gap-4 w-full">
          {/* Logo */}
          <Link to="/" className="flex items-center group shrink-0 relative z-50" data-testid="logo-link">
            <div className="relative">
              <img 
                src={logo} 
                alt="YoursDreamDecoration" 
                className={cn(
                  "h-14 sm:h-18 w-auto transition-all duration-700 ease-[cubic-bezier(0.23,1,0.32,1)] group-hover:scale-[2.2] group-hover:rotate-[5deg] group-hover:drop-shadow-[0_20px_30px_rgba(236,72,153,0.4)] relative",
                  isScrolled ? "brightness-100" : "brightness-0 invert"
                )} 
              />
              <div className="absolute inset-0 bg-primary/20 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-1000" />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-5 text-[13px] font-bold uppercase tracking-wider">
            <Link
              to="/packages"
              className={cn(
                "py-2 transition-all hover:text-primary flex items-center gap-1 magnetic-link relative",
                location.pathname === "/packages" ? "text-primary" : isScrolled ? "text-slate-700" : "text-white drop-shadow-[0_2px_5px_rgba(0,0,0,0.8)]"
              )}
            >
              All Packages
            </Link>
            <Link
              to="/blog"
              className={cn(
                "py-2 transition-all hover:text-primary flex items-center gap-1 magnetic-link relative",
                location.pathname === "/blog" ? "text-primary" : isScrolled ? "text-slate-700" : "text-white drop-shadow-[0_2px_5px_rgba(0,0,0,0.8)]"
              )}
            >
              Blog
            </Link>

            {/* Per-category mega menus (each main decoration category) */}
            {topDecoCats.map((cat) => {
              const catPkgs = packagesByCategory[cat.slug] || [];
              const featured = catPkgs.length ? catPkgs[rotateIdx % catPkgs.length] : null;
              const subs = cat.subcategories || [];
              const isActive = activeMega === cat.slug;
              return (
                <div
                  key={cat.slug}
                  className="relative h-full flex items-center"
                  onMouseEnter={() => { setActiveMega(cat.slug); setRotateIdx(Math.floor(Math.random() * 1000)); }}
                  onMouseLeave={() => setActiveMega(null)}
                  data-testid={`nav-cat-${cat.slug}`}
                >
                  <Link
                    to={`/category/${cat.slug}`}
                    className={cn(
                      "py-2 transition-all hover:text-primary flex items-center gap-1 cursor-pointer magnetic-link relative",
                      isScrolled ? "text-slate-700" : "text-white drop-shadow-[0_2px_5px_rgba(0,0,0,0.8)]"
                    )}
                  >
                    {cat.name} <ChevronDown className={cn("w-3.5 h-3.5 transition-transform", isActive ? "rotate-180" : "")} />
                  </Link>

                  <div className={cn(
                    "absolute top-full left-0 mt-2 w-[640px] bg-white/95 backdrop-blur-2xl rounded-3xl shadow-2xl border border-white/30 overflow-hidden transition-all duration-300 origin-top z-[100]",
                    isActive ? "opacity-100 scale-100 visible" : "opacity-0 scale-95 invisible pointer-events-none"
                  )}>
                    <div className="grid grid-cols-2">
                      {/* LEFT: subcategories list */}
                      <div className="p-5 border-r border-slate-100 bg-slate-50/40">
                        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400 mb-3 px-1">
                          {subs.length > 0 ? "Sub-Categories" : "Browse"}
                        </div>
                        <div className="grid gap-1">
                          {subs.length > 0 ? subs.map((s) => (
                            <Link
                              key={s.slug}
                              to={`/category/${cat.slug}?sub=${s.slug}`}
                              className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-primary/10 hover:translate-x-1 text-sm font-bold text-slate-700 hover:text-primary transition-all group"
                              data-testid={`nav-sub-${s.slug}`}
                            >
                              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-all shrink-0">
                                <Sparkles className="w-3.5 h-3.5" />
                              </div>
                              <span className="flex-1">{s.name}</span>
                              <ChevronRight className="w-3.5 h-3.5 text-slate-300 group-hover:translate-x-1 transition-transform" />
                            </Link>
                          )) : (
                            <Link
                              to={`/category/${cat.slug}`}
                              className="flex items-center gap-3 p-3 rounded-xl hover:bg-primary/10 text-sm font-bold text-slate-700 hover:text-primary transition-all"
                            >
                              <Sparkles className="w-4 h-4" /> View All {cat.name}
                            </Link>
                          )}
                          <Link
                            to={`/category/${cat.slug}`}
                            className="mt-2 flex items-center justify-between p-2.5 rounded-xl border border-primary/10 text-xs font-black uppercase tracking-wider text-primary hover:bg-primary hover:text-white transition-all"
                          >
                            See all in {cat.name}
                            <ChevronRight className="w-3.5 h-3.5" />
                          </Link>
                        </div>
                      </div>

                      {/* RIGHT: rotating featured package */}
                      <div className="p-4">
                        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400 mb-3 flex items-center gap-2">
                          <Sparkles className="w-3 h-3 text-primary" />
                          Featured pick
                        </div>
                        {featured ? (
                          <Link
                            to={`/package/${featured.slug}`}
                            key={featured.slug}
                            className="block group rounded-2xl overflow-hidden bg-white border border-slate-100 hover:shadow-2xl hover:-translate-y-1 transition-all duration-500"
                            data-testid={`nav-featured-${cat.slug}`}
                          >
                            <div className="relative h-40 overflow-hidden bg-slate-100">
                              {featured.images?.[0] && (
                                <img
                                  src={featured.images[0]}
                                  alt={featured.title}
                                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                />
                              )}
                              <div className="absolute top-2 right-2 px-2.5 py-1 bg-white/90 backdrop-blur rounded-full text-[9px] font-black text-primary uppercase tracking-wider">
                                Hot
                              </div>
                              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                            </div>
                            <div className="p-3">
                              <div className="text-sm font-bold line-clamp-2 text-slate-800 leading-snug">
                                {featured.title}
                              </div>
                              <div className="flex items-center justify-between mt-2">
                                <div className="text-base font-black text-primary">
                                  ₹{Number(featured.price).toLocaleString("en-IN")}
                                </div>
                                <div className="text-[10px] font-bold uppercase text-primary opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                                  View <ChevronRight className="w-3 h-3" />
                                </div>
                              </div>
                            </div>
                          </Link>
                        ) : (
                          <div className="text-xs text-slate-400 italic p-4">No featured package yet</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Activities & extras (single dropdown, kept compact) */}
            {(activityCategories.length > 0 || moreDecoCats.length > 0) && (
              <div
                className="relative h-full flex items-center"
                onMouseEnter={() => setActiveMega("activities")}
                onMouseLeave={() => setActiveMega(null)}
              >
                <div className={cn(
                  "py-2 transition-all hover:text-primary flex items-center gap-1 cursor-pointer magnetic-link relative",
                  isScrolled ? "text-slate-700" : "text-white drop-shadow-[0_2px_5px_rgba(0,0,0,0.8)]"
                )}>
                  More <ChevronDown className={cn("w-3.5 h-3.5 transition-transform", activeMega === "activities" ? "rotate-180" : "")} />
                </div>

                <div className={cn(
                  "absolute top-full right-0 mt-2 w-72 bg-white/95 backdrop-blur-2xl rounded-3xl shadow-2xl border border-white/20 p-3 transition-all duration-300 origin-top z-[100]",
                  activeMega === "activities" ? "opacity-100 scale-100 visible" : "opacity-0 scale-95 invisible pointer-events-none"
                )}>
                  <div className="grid gap-1">
                    {[...moreDecoCats, ...activityCategories].map(c => (
                      <Link
                        key={c.slug}
                        to={`/category/${c.slug}`}
                        className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-primary/5 text-sm font-bold text-slate-700 hover:text-primary transition-all group"
                      >
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-all shrink-0">
                          <Sparkles className="w-3.5 h-3.5" />
                        </div>
                        <span className="flex-1">{c.name}</span>
                        <ChevronRight className="w-3.5 h-3.5 text-slate-300 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Redundant links removed */}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="hidden md:block relative">
              <form onSubmit={submit} className={cn(
                "flex items-center rounded-full px-4 py-2 gap-2 transition-all duration-300",
                isScrolled ? "bg-slate-100" : "bg-white/10 backdrop-blur border border-white/20",
                searchOpen ? "w-64" : "w-10 px-0 justify-center"
              )}>
                <button type="button" aria-label="Search" onClick={() => setSearchOpen(!searchOpen)} className={isScrolled ? "text-slate-600" : "text-white"}>
                  <Search className="w-5 h-5" />
                </button>
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search magic..."
                  className={cn(
                    "bg-transparent outline-none text-sm font-medium",
                    isScrolled ? "text-slate-900" : "text-white placeholder:text-white/50",
                    searchOpen ? "w-full opacity-100" : "w-0 opacity-0 overflow-hidden"
                  )}
                />
              </form>

              {/* Fuzzy Search Results Popover */}
              {searchOpen && searchResults.length > 0 && (
                <div className="absolute top-full mt-4 right-0 w-80 bg-white rounded-3xl shadow-2xl border border-slate-100 overflow-hidden z-[60] animate-in fade-in slide-in-from-top-2">
                  <div className="p-2">
                    {searchResults.map(p => (
                      <Link
                        key={p.slug}
                        to={`/package/${p.slug}`}
                        onClick={() => { setSearchOpen(false); setQ(""); }}
                        className="flex items-center gap-3 p-3 rounded-2xl hover:bg-slate-50 transition-all group"
                      >
                        <img src={p.images?.[0]} alt="" className="w-12 h-12 rounded-xl object-cover" />
                        <div className="flex-1 overflow-hidden">
                          <div className="text-sm font-bold text-slate-800 truncate">{p.title}</div>
                          <div className="text-xs text-primary font-bold">₹{p.price}</div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-300 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a href={waLink("Hi")} target="_blank" rel="noreferrer" className="hidden sm:block">
              <Button className="rounded-full px-6 bg-[#25D366] hover:bg-[#22c35e] text-white shadow-lg shadow-green-500/20 gap-2 font-bold border-none group h-12">
                <MessageCircle className="w-5 h-5 group-hover:scale-110 transition-transform" /> 
                <span className="hidden md:inline">WhatsApp</span>
              </Button>
            </a>

            <div className="hidden sm:block">
              <ThemeToggle />
            </div>

            <button aria-label="Toggle Menu" className={cn("lg:hidden p-2 rounded-full", isScrolled ? "text-slate-900" : "text-white")} onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
          </div> {/* end foreground z-10 wrapper */}
        </div>

      </header>

      {/* Mobile Menu Overlay */}
      <div 
        className={cn(
          "fixed inset-0 z-[60] bg-background/95 backdrop-blur-xl lg:hidden transition-all duration-500 flex flex-col",
          mobileMenuOpen ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"
        )}
      >
        <div className="flex items-center justify-between px-6 py-6 border-b">
          <span className="font-serif-display text-2xl font-semibold">Menu</span>
          <button onClick={() => setMobileMenuOpen(false)} className="p-2 bg-muted/60 rounded-full">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto px-6 py-8 space-y-6">
          <form onSubmit={submit} className="flex items-center bg-muted/60 rounded-2xl px-5 py-4 gap-3 mb-8">
            <Search className="w-5 h-5 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="What are you looking for?"
              className="bg-transparent flex-1 outline-none text-lg"
            />
          </form>

          <Link
            to="/packages"
            onClick={() => setMobileMenuOpen(false)}
            className="flex items-center justify-between py-5 px-6 rounded-2xl bg-primary text-white shadow-xl shadow-primary/20 mb-8 text-xl font-bold"
          >
            <div className="flex items-center gap-3">
              <Sparkles className="w-6 h-6" />
              Explore All Packages
            </div>
            <ChevronRight className="w-6 h-6" />
          </Link>

          <div className="space-y-8">
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-4 px-2">Decoration</p>
              <div className="grid grid-cols-1 gap-2">
                {decorationCategories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center justify-between py-4 px-4 rounded-2xl bg-muted/40 hover:bg-muted transition-colors text-lg font-bold"
                  >
                    <div className="flex items-center gap-3">
                      <img src={cat.image} alt="" className="w-10 h-10 rounded-xl object-cover" />
                      {cat.name}
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </Link>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-4 px-2">Activities</p>
              <div className="grid grid-cols-2 gap-3">
                {activityCategories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex flex-col items-center justify-center py-6 px-4 rounded-2xl bg-primary/5 hover:bg-primary/10 transition-all group border border-primary/10"
                  >
                    <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-primary mb-3 shadow-sm group-hover:scale-110 transition-transform">
                      <Sparkles className="w-5 h-5" />
                    </div>
                    <span className="text-xs font-bold text-center text-slate-700 uppercase tracking-tight truncate w-full">{cat.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 border-t bg-muted/20">
          <a href={waLink("Hi")} target="_blank" rel="noreferrer">
            <Button className="w-full h-14 rounded-2xl bg-[#25D366] hover:bg-[#22c35e] text-white text-lg font-bold gap-2">
              <MessageCircle className="w-6 h-6" /> WhatsApp Us
            </Button>
          </a>
        </div>
      </div>

      {/* Spacer to prevent content jump since header is fixed */}
      {!isScrolled && <div className="h-20 sm:h-24 hidden lg:block" />}
    </>
  );
};

export const Footer = () => (
  <footer className="bg-[#0D1117] text-slate-300 mt-24 border-t border-white/5" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-6 sm:px-8 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 sm:gap-8">
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <img src={logo} alt="Logo" className="h-12 w-auto brightness-0 invert opacity-80" />
          <div className="font-serif-display text-3xl text-white font-medium">YoursDreamDecoration</div>
        </div>
        <p className="text-sm leading-relaxed opacity-80">
          Crafting unforgettable celebrations across India. We bring magic to your birthdays, anniversaries, baby showers, and festivals with premium decorations.
        </p>
        <div className="flex gap-4">
          {/* Social icons placeholder */}
          <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-primary/20 hover:text-primary transition-colors cursor-pointer">
            <div className="w-5 h-5 border-2 border-current rounded-sm" />
          </div>
          <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-primary/20 hover:text-primary transition-colors cursor-pointer">
            <div className="w-5 h-5 border-2 border-current rounded-full" />
          </div>
        </div>
      </div>
      
      <div>
        <h4 className="font-semibold mb-6 text-white text-sm uppercase tracking-[0.2em]">Quick Explore</h4>
        <ul className="space-y-4 text-sm">
          <li><Link to="/packages" className="hover:text-primary transition-colors">All Packages</Link></li>
          <li><Link to="/blog" className="hover:text-primary transition-colors font-bold text-primary flex items-center gap-1"><Sparkles size={14}/> Decoration Blog</Link></li>
          <li><Link to="/category/kid-s-special" className="hover:text-primary transition-colors">Birthday Magic</Link></li>
          <li><Link to="/category/couple-s-special" className="hover:text-primary transition-colors">Romantic Surprises</Link></li>
          <li><Link to="/category/premium-setup" className="hover:text-primary transition-colors">Anniversary Setups</Link></li>
          <li><Link to="/category/festival-decorations" className="hover:text-primary transition-colors">Festival Decor</Link></li>
        </ul>
      </div>

      <div>
        <h4 className="font-semibold mb-6 text-white text-sm uppercase tracking-[0.2em]">Information</h4>
        <ul className="space-y-4 text-sm">
          <li><Link to="/about" className="hover:text-primary transition-colors">About Our Story</Link></li>
          <li><Link to="/how-it-works" className="hover:text-primary transition-colors">How It Works</Link></li>
          <li><Link to="/booking-policy" className="hover:text-primary transition-colors">Booking Policy</Link></li>
          <li><Link to="/admin/login" className="hover:text-primary transition-colors">Partner/Admin Login</Link></li>
        </ul>
      </div>

      <div className="space-y-6">
        <h4 className="font-semibold mb-6 text-white text-sm uppercase tracking-[0.2em]">Contact Us</h4>
        <div className="space-y-4 text-sm">
          <div className="flex items-start gap-3">
            <div className="mt-1 w-5 h-5 text-primary shrink-0" />
            <span>+91 9654861504</span>
          </div>
          <div className="flex items-start gap-3">
            <div className="mt-1 w-5 h-5 text-primary shrink-0" />
            <span>yoursdreamdecoration@gmail.com</span>
          </div>
          <div className="flex items-start gap-3">
            <div className="mt-1 w-5 h-5 text-primary shrink-0" />
            <span className="opacity-80 leading-relaxed">Noida · Delhi · Greater Noida · Ghaziabad · Indirapuram</span>
          </div>
          <div className="flex items-start gap-3">
            <div className="mt-1 w-5 h-5 text-primary shrink-0" />
            <span className="opacity-80 leading-relaxed text-xs">Janta flat, Shramik Kunj, Sector 122, Noida, UP 201316</span>
          </div>
        </div>
      </div>
    </div>
    <div className="border-t border-white/5 py-8 text-center text-xs opacity-40">
      © {new Date().getFullYear()} YoursDreamDecoration Private Limited. All rights reserved.
    </div>
  </footer>
);

export const PublicLayout = ({ children }) => (
    <div className="min-h-screen grainy-texture bg-background text-foreground selection:bg-primary/20">
      <Header />
      <main className="pt-20 sm:pt-24 min-h-[calc(100vh-200px)]">
        {children}
      </main>
      <Footer />
    </div>
);
